import firebase from "../../firebase"
import { CollectionNames } from "../."

import type { Answer } from "./Answer"
import type { AnswerState } from "./AnswerState"
import type { AssetRef } from "./AssetRef"
import type { BlockRef } from "./BlockRef"
import type { BlockState } from "./BlockState"
import type { Duration } from "./Duration"
import type { ElementState } from "./ElementState"
import type { ExerciseState } from "./ExerciseState"
import type { Feedback } from "./Feedback"
import type { FeedbackState } from "./FeedbackState"
import type { Hint } from "./Hint"
import type { Jump } from "./Jump"
import type { Other } from "./Other"
import type { Question } from "./Question"
import type { QuestionState } from "./QuestionState"
import type { QuestionTarget } from "./QuestionTarget"
import type { SeqRef } from "./SeqRef"
import type { ScreenState } from "./ScreenState"
import type { Settings } from "./Settings"
import type { SnapPath } from "./SnapPath"
import type { SnapRef } from "./SnapRef"
import type { SnapShotRef } from "./SnapShotRef"
import type { TTS } from "./TTS"

import type { Preferences, VideoSettings } from "../collections/users"

import {
  BlockPresentation,
  BlockVideo,
  BlockInteraction,
  BlockEmpty,
  BlockImage,
  BlockPdf,
  BlockText,
  Media,
  BlockQtiInteraction
} from "../collections"
import { Explanation } from "./Explanation"

// Key Types
export type AssetType =
  | "image"
  | "mux"
  | "thumbnail"
  | "blob"
  | "word-bank"
  | "pdf"
  | "content-resource"
  | "mux_subtitles"
  | "mux_audio"
  | "audio_description"

export type CourseType =
  | "general"
  | "essay"
  | "english"
  | "math"
  | "reading"
  | "elar"
  | "science"
  | "writing"
  | "algebra1"
  | "algebra2"
  | "english1"
  | "english2"
  | "ushistory"
  | "biology"
  | "geometry"
  | "socialstudies"
  | "math3"

export type BlockTypeMap = {
  presentation: BlockPresentation
  qtiInteraction: BlockQtiInteraction
  interaction: BlockInteraction
  video: BlockVideo
  image: BlockImage
  pdf: BlockPdf
  text: BlockText
  empty: BlockEmpty
}

export type BlockType = keyof BlockTypeMap

export type InteractionOrientation = "vertical" | "horizontal"

export type AnswerType =
  | "text"
  | "rich-text"
  | "math"
  | "image"
  | "video"
  | "text-zone"
  | "image-zone"
  | "non-zone"
  | "word-bank"
  | "rate"

export type QtiInteractionType =
  | "choice"
  | "gapmatch"
  | "match"
  | "hotspot"
  | "hottext"
  | "order"
  | "extendedtext"
  | "equationEditor"
  | "inlineChoice"
  | "textEntry"

export type InteractionType =
  | "mini-test"
  | "multiple-choice"
  | "poll"
  | "clickzone"
  | "assessment"
  | "rate"
  | "sort"
  | "matching"
  | "constructed-response"
  | "matching"
  | "multiple-select"
  | "multiple-clickzone"
  | "sequence"
  | "word-cloud"

export type MediaType =
  | "ppt"
  | "video"
  | "image"
  | "pdf"
  | "word-bank"
  | "content-resource"

export type HintType = "video" | "text" | "video+text"

export type SequenceType =
  | "config"
  | "logic"
  | "screen"
  | "exercise"
  | "assessment"
  | "qtiScreen"
  | "passage"

export type SnapType = "snap" | "element" | "assessment"

// export type LayoutType = 'Large Left' | 'Large Right' | 'Balanced' | 'Centered Column'//

export type LayoutType =
  | "Centered Column"
  | "Balanced"
  | "Large Left"
  | "Large Right"

export type MobileLayout = "Stacked" | "Tabbed"

export type TemplateType =
  | "presentation"
  | "presentation+video"
  | "text+question"

export type FeedbackType = "text" | "text+video" | "video"

export type TextType =
  | "passage"
  | "citation"
  | "header"
  | "table"
  | "opentext"
  | "instruction"

export type PresentationType = "fade" | "slide" | "none"

export type JumpType = "branch" | "snap" | ""

export type QuestionTargetType = "image" | "text"

export type QuestionTargetSubType = "passage" | "image" | "opentext"

export type FirestoreTimestampType = Pick<
  firebase.firestore.Timestamp,
  "seconds" | "nanoseconds"
>

export type MetaType = {
  owner_id: string
  updated: FirestoreTimestampType
  created: FirestoreTimestampType
  deleted: boolean
  last_updated_by: string
  hide?: boolean
}

export type TopicMap = { [k: string]: Array<SnapRef> }

export type BranchMap = { [k: string]: Array<SeqRef> }

export type { CropBox, CropType } from "../schemas"

export type CropSizes = "xs" | "sm" | "md" | "lg" | "xl"

export type CustomTheme = {
  dark: string
  primaryColor: string
  secondaryColor: string
}

export type TypeArray = {
  Answer: Answer
  AnswerState: AnswerState
  Explanation: Explanation
  AssetRef: AssetRef
  BlockRef: BlockRef
  BlockState: BlockState
  Settings: Settings
  create: null
  delete: number
  null: null
  reset: null
  Duration: Duration
  ElementState: ElementState
  ExerciseState: ExerciseState
  Feedback: Feedback
  FeedbackState: FeedbackState
  Hint: Hint
  ScreenState: ScreenState
  SeqRef: SeqRef
  SnapRef: SnapRef
  SnapShotRef: SnapShotRef
  Question: Question
  QuestionState: QuestionState
  SnapPath: SnapPath
  Other: Other
  Jump: Jump
  QuestionTarget: QuestionTarget
  TTS: TTS
  WordDurations: WordDurations
}

export type CollectionBlockTypeMap = {
  block_interactions: "interaction"
  block_qti_interactions: "qtiInteraction"
  block_presentations: "presentation"
  block_videos: "video"
  block_pdfs: "pdf"
  block_images: "image"
  block_texts: "text"
  block_empty: "empty"
}

type CollectionNameBlockTypeMap = { [key in BlockType]: CollectionNames }

export const collectionMap: CollectionNameBlockTypeMap = {
  interaction: "block_interactions",
  qtiInteraction: "block_qti_interactions",
  presentation: "block_presentations",
  video: "block_videos",
  pdf: "block_pdfs",
  image: "block_images",
  text: "block_texts",
  empty: "block_empty"
}

export type CropDataType = {
  x: number
  y: number
  width: number
  height: number
  rotate: number
  scaleX: number
  scaleY: number
}

export type ElementStateMap = { [key: string]: ElementState }

export type ExerciseStateMap = { [key: string]: ExerciseState }

export type TrimDataType = {
  start: number
  end: number
}

export type {
  AssetRef,
  Answer,
  AnswerState,
  BlockRef,
  BlockState,
  Duration,
  ElementState,
  ExerciseState,
  Feedback,
  FeedbackState,
  Hint,
  Jump,
  Other,
  ScreenState,
  Settings,
  SeqRef,
  SnapPath,
  SnapRef,
  SnapShotRef,
  Question,
  QuestionState,
  QuestionTarget,
  Preferences,
  VideoSettings,
  TTS
}

export type SnapConfig = {
  name: string
  group: string
  duration: number
  interactionNumber?: number
  practiceQuestionsNumber?: number
  description?: string
}

export type ReadAloud = {
  lastSynthesized: { seconds: number; nanoseconds: number } | null
  enabled: boolean
}

export type LibraryAssetData = {
  id: string
  media: Media
  url: string
}
